'use client';

import type { ReactNode } from 'react';
import { useId, useState } from 'react';

import type { IconName } from '@eliadmin/core/src/components/svetech/ui/icon';

import { Icon } from '@eliadmin/core/src/components/svetech/ui/icon';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type HeroSlide = {
  id: number;
  /** Product photo (server-rendered `CmsImage`, fills its positioned parent). */
  media: ReactNode;
  /** Title and description shown in the glass card. */
  content: ReactNode;
};

const pad = (value: number) => String(value).padStart(2, '0');

/**
 * Figma 4451:1512 (photo) + 4451:1513 (glass card) of the homepage hero. Below `lg` the photo is a
 * rounded box under the copy with the card on top of it (mobile 4702:4161: 320 px tall, the 289 px
 * card in its bottom right corner). From `lg` the wrapper is
 * `display: contents`: the photo is positioned against the section and bleeds to its right edge,
 * the card is the second column of the hero container. Controls appear from two slides on.
 */
export function HeroSlider({ slides }: { slides: HeroSlide[] }) {
  const { t } = useTranslations();
  const [active, setActive] = useState(0);
  const contentId = useId();
  const count = slides.length;
  const multiple = count > 1;

  const go = (step: number) => setActive(index => (index + step + count) % count);

  return (
    <div className="relative flex min-h-[320px] flex-col justify-end overflow-hidden rounded-[32px] rounded-tr-none sm:min-h-[440px] md:min-h-[520px] md:p-6 lg:contents">
      <div className="absolute inset-0 lg:left-auto lg:w-[55.85%]">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            aria-hidden={index !== active}
            className={cn(
              'absolute inset-0 transition-opacity duration-500 motion-reduce:transition-none',
              index === active ? 'opacity-100' : 'opacity-0',
            )}
          >
            {slide.media}
          </div>
        ))}
        {/* Mobile only: keeps the white card text readable on light photos. */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-b from-transparent to-black/40 lg:hidden"
        />
        {/* 965 / 1728 = 55.85 % wide photo fading into the background on its left 16 %. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 hidden bg-gradient-to-l from-[rgba(242,243,239,0)] from-[84%] to-[#f2f3ef] lg:block"
        />
      </div>

      <div
        role={multiple ? 'group' : undefined}
        aria-roledescription={multiple ? 'karusel' : undefined}
        aria-label={multiple ? t('text3524') : undefined}
        className="relative z-10 flex w-[289px] max-w-full flex-col gap-3 self-end rounded-[24px] rounded-tr-none bg-black/10 p-4 backdrop-blur-[5px] md:w-full md:max-w-[539px] md:gap-6 md:self-stretch md:rounded-[32px] md:rounded-tr-none md:p-6 lg:self-end"
      >
        <div id={contentId} className="grid">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={cn(
                'col-start-1 row-start-1 transition-[opacity,visibility] duration-300 motion-reduce:transition-none',
                index === active ? 'visible opacity-100' : 'invisible opacity-0',
              )}
            >
              {slide.content}
            </div>
          ))}
        </div>

        {multiple ? (
          <div className="relative z-10 flex items-center gap-6">
            <p aria-hidden="true" className="svt-label-s shrink-0 text-white">
              {pad(active + 1)} / {pad(count)}
            </p>
            <p aria-live="polite" className="sr-only">
              {`Snímka ${active + 1} z ${count}`}
            </p>
            <span aria-hidden="true" className="flex h-0.5 min-w-0 flex-1 bg-white/30">
              {slides.map((slide, index) => (
                <span
                  key={slide.id}
                  className={cn(
                    'flex-1 transition-colors duration-300 motion-reduce:transition-none',
                    index <= active && 'bg-[#f1c40f]',
                  )}
                />
              ))}
            </span>
            <div className="flex shrink-0 items-center gap-2">
              <SlideButton
                icon="chevron-left"
                label={t('text3525')}
                controls={contentId}
                onClick={() => go(-1)}
              />
              <SlideButton
                icon="chevron-right"
                label={t('text3526')}
                controls={contentId}
                onClick={() => go(1)}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

type SlideButtonProps = {
  icon: IconName;
  label: string;
  controls: string;
  onClick: () => void;
};

/** 20 px chevron from Figma; the hit area is enlarged to 28 × 40 px by the pseudo-element. */
function SlideButton({ icon, label, controls, onClick }: SlideButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-controls={controls}
      onClick={onClick}
      className="relative flex size-5 items-center justify-center rounded-sm text-white transition-colors before:absolute before:-inset-x-1 before:-inset-y-2.5 hover:text-[#f1c40f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white motion-reduce:transition-none"
    >
      <Icon name={icon} className="size-5" />
    </button>
  );
}
